import { API_BASE_URL, API_CORE_BASE_URL, EnumOfflineMode, FEED_OFFLINE_MODE, IS_BINARY } from '@/constants';
import { CONFIG } from '@/global-config';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { IRequestProps, IResponseProps } from '../instances/types';
import { fetchRequest } from './fetch';
import { forceLogout, getOldRefresh, isInvalidTokenError, runRefresh } from './refresh';

export const AxiosInstance = Axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// chama PUT /auth direto no axios (sem passar por request) p/ nao re-entrar nessa logica
function refreshAuthToken(): Promise<string> {
  return runRefresh(async () => {
    const oldRefresh = getOldRefresh();
    if (!oldRefresh) throw new Error('Refresh token não encontrado');
    const { data } = await AxiosInstance.put('/auth', { oldRefresh });
    return data;
  });
}

export const CoreInstance = Axios.create({
  baseURL: API_CORE_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const onUnauthorizedResponse = async (error: AxiosError) => {
  const hadAuth = Boolean(error.config?.headers?.Authorization);

  if (error.response?.status === 401 && hadAuth) {
    const { handleSessionExpired } = await import('@/middleware/session-expired');
    handleSessionExpired();
  }

  return Promise.reject(error);
};

AxiosInstance.interceptors.response.use((response) => response, onUnauthorizedResponse);
CoreInstance.interceptors.response.use((response) => response, onUnauthorizedResponse);

export async function request<T>(config: IRequestProps): Promise<IResponseProps<T>> {
  const offlineModeStorage = localStorage.getItem(FEED_OFFLINE_MODE);
  const offlineMode = JSON.parse(offlineModeStorage || 'false');

  const headers = new Headers({
    'content-type': 'application/json; charset=utf-8'
  });

  const authorization = AxiosInstance.defaults.headers.common['Authorization'];
  const farmDay = AxiosInstance.defaults.headers.common['farm-day'];

  if (authorization) headers.append('Authorization', authorization as string);
  if (farmDay) headers.append('FARM_DAY', farmDay as string);

  const params = Object.entries(config.params || {}).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  if (!IS_BINARY) {
    const ALLOWED_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;
    type AxiosMethod = (typeof ALLOWED_METHODS)[number];

    const method = config.method.toLowerCase() as AxiosMethod;

    if (!ALLOWED_METHODS.includes(method)) {
      throw new Error('Método de requisição inválido');
    }

    const axiosConfig: AxiosRequestConfig = {
      headers: {
        Accept: config.responseType || 'application/json',
        'x-fdm-app-version': CONFIG.appVersion,
        ...(config.headers &&
          Object.fromEntries(
            Object.entries(config.headers).filter(
              ([_, value]) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
            )
          ))
      },
      responseType: config.responseType as any,
      ...(config.signal ? { signal: config.signal } : {})
    };

    if (config.params) {
      const paramsWithData = Object.fromEntries(
        Object.entries(config.params || {}).filter(([_, value]) => value !== undefined)
      );
      const queryParams = { isWeb: 'true', ...paramsWithData };
      const queryString = new URLSearchParams(queryParams).toString();
      config.url = `${config.url}?${queryString}`;
    }

    const executeRequest = async (): Promise<IResponseProps<T>> => {
      if (['post', 'put', 'patch'].includes(method)) {
        return AxiosInstance[method](config.url, config.data, axiosConfig);
      }
      if (method === 'delete') {
        axiosConfig.data = config.data;
        return AxiosInstance.delete(config.url, axiosConfig);
      }
      return AxiosInstance[method](config.url, axiosConfig);
    };

    // nao tenta refresh no proprio /auth p/ evitar loop
    const isAuthRoute = config.url.includes('/auth');

    try {
      return await executeRequest();
    } catch (error: any) {
      const status = error?.response?.status ?? error?.statusCode;
      const message = error?.response?.data?.message ?? error?.message;

      // So tenta refresh se a request original saiu com Authorization
      // caminho fetch: 401 sem header nao e token expirado).
      const hadAuthHeader = Boolean(error?.config?.headers?.Authorization);

      if (hadAuthHeader && isInvalidTokenError(status, message) && !isAuthRoute) {
        try {
          await refreshAuthToken();
          // header Authorization ja atualizado no defaults; retry uma vez
          return await executeRequest();
        } catch {
          forceLogout();
          // segue p/ lançar o erro original abaixo
        }
      }

      if (!error?.response?.data) {
        throw error;
      }

      throw error.response.data;
    }
  }

  if (offlineMode === EnumOfflineMode.synced) {
    // import dinamico quebra o ciclo query.ts -> instances -> useCases offline -> query.ts (TDZ no bundle)
    const { indexedDBRequest } = await import('./indexedDB');
    const response = await indexedDBRequest<T>({
      ...config,
      headers,
      params
    });

    return response;
  }

  const response = await fetchRequest<T>({
    ...config,
    headers,
    params
  });

  return response;
}

export const coreInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = CoreInstance({
    ...config,
    ...options,
    cancelToken: source.token
  }).then(({ data }) => data);

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export type ErrorType<Error> = AxiosError<Error>;
